import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ShieldAlert,
  Ban,
  Lock,
  WifiOff,
  KeyRound,
  RefreshCw,
  AlertTriangle,
  Terminal,
  Activity,
  CheckCircle2,
  X,
  Radio,
  Cpu,
} from 'lucide-react';
import { BlockedIpEntry, CoupleSettings, VisitorInfo } from '../types';

interface BlockedIpScreenProps {
  blockedIp: string;
  blockedReason?: string;
  blockedEntry?: BlockedIpEntry;
  visitorInfo?: VisitorInfo | null;
  settings?: CoupleSettings;
  onEmergencyAdminUnlock?: (email: string, pass: string) => Promise<boolean> | boolean;
  onRefreshStatus?: () => void;
}

export const BlockedIpScreen: React.FC<BlockedIpScreenProps> = ({
  blockedIp,
  blockedReason,
  blockedEntry,
  visitorInfo,
  settings,
  onEmergencyAdminUnlock,
  onRefreshStatus,
}) => {
  const [showAdminUnlockModal, setShowAdminUnlockModal] = useState(false);
  const [adminEmailInput, setAdminEmailInput] = useState('');
  const [adminPasswordInput, setAdminPasswordInput] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isShaking, setIsShaking] = useState(false);

  // Generate a deterministic incident ticket code based on IP
  const incidentCode = React.useMemo(() => {
    let hash = 0;
    const str = `${blockedIp}_SUSPICIOUS_ACTIVITY`;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return `SEC-${Math.abs(hash).toString(16).toUpperCase().padStart(8, '0')}`;
  }, [blockedIp]);

  const effectiveReason =
    blockedReason ||
    blockedEntry?.reason ||
    'Actividad sospechosa detectada por el sistema de seguridad';

  const handleAdminUnlockSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminEmailInput.trim() || !adminPasswordInput.trim() || !onEmergencyAdminUnlock) return;

    setIsUnlocking(true);
    setUnlockError('');

    try {
      const success = await onEmergencyAdminUnlock(adminEmailInput.trim(), adminPasswordInput.trim());
      if (!success) {
        setIsShaking(true);
        setUnlockError('Credenciales incorrectas o sin rol administrativo. Intento registrado.');
        setTimeout(() => setIsShaking(false), 500);
      } else {
        setShowAdminUnlockModal(false);
      }
    } catch {
      setIsShaking(true);
      setUnlockError('Error al procesar el desbloqueo.');
      setTimeout(() => setIsShaking(false), 500);
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleManualRefresh = () => {
    setIsRefreshing(true);
    if (onRefreshStatus) {
      onRefreshStatus();
    } else {
      window.location.reload();
    }
    setTimeout(() => setIsRefreshing(false), 1200);
  };

  return (
    <div
      id="blocked-ip-screen"
      className="min-h-screen w-full bg-[#FFFDFB] text-[#302B30] flex flex-col justify-between items-center p-4 sm:p-6 lg:p-8 font-['Plus_Jakarta_Sans',sans-serif] relative overflow-hidden select-none"
    >
      {/* Top Security Header */}
      <header className="w-full max-w-2xl flex items-center justify-between border-b border-[#F0DDE4] pb-4 z-10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 shadow-2xs">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div>
            <span className="text-xs font-mono font-bold tracking-wider uppercase text-rose-700 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping inline-block" />
              Sistema de Protección Activo
            </span>
            <p className="text-[11px] text-[#77717A]">Cortafuegos & Control de Acceso</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden sm:inline-block px-2.5 py-1 rounded-full text-[10px] font-mono font-bold uppercase bg-rose-50 text-rose-700 border border-rose-200">
            Código 403 • Restringido
          </span>
          <button
            onClick={handleManualRefresh}
            title="Reintentar verificación"
            className="p-2 rounded-xl bg-white hover:bg-[#FFF4F7] text-[#77717A] hover:text-[#302B30] border border-[#F0DDE4] transition-colors cursor-pointer shadow-2xs"
            id="btn-refresh-ip-status"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin text-rose-600' : ''}`} />
          </button>
        </div>
      </header>

      {/* Main Lockout Notice Container */}
      <main className="w-full max-w-2xl my-auto py-8 z-10 space-y-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="bg-white border border-rose-200 rounded-3xl p-6 sm:p-8 shadow-[0_12px_40px_rgba(244,63,94,0.08)] relative overflow-hidden"
        >
          {/* Subtle Red Strip */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-rose-500 to-rose-600" />

          {/* Alert Icon and Primary Title */}
          <div className="text-center space-y-3 pt-2">
            <div className="w-18 h-18 sm:w-20 sm:h-20 rounded-3xl bg-rose-50 border border-rose-200 flex items-center justify-center mx-auto text-rose-600 shadow-sm relative">
              <Ban className="w-10 h-10 sm:w-11 sm:h-11 text-rose-600" />
              <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center text-[10px] font-bold shadow-xs">
                !
              </div>
            </div>

            <div className="space-y-1.5">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-[#302B30] font-['Playfair_Display',serif]">
                Acceso Restringido por Seguridad
              </h1>
              <p className="text-xs sm:text-sm text-[#77717A] max-w-lg mx-auto leading-relaxed">
                El acceso a este sitio web ha sido restringido para tu conexión. Se ha detectado
                actividad inusual procedente de tu dirección de red.
              </p>
            </div>
          </div>

          {/* Detailed Security Incident Card */}
          <div className="mt-6 rounded-2xl bg-[#FFF4F7] border border-[#F0DDE4] p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between border-b border-[#F0DDE4] pb-2.5">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#302B30] flex items-center gap-1.5">
                <Terminal className="w-3.5 h-3.5 text-rose-600" />
                Registro de Seguridad
              </span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white text-rose-800 border border-rose-200 font-bold">
                {incidentCode}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="space-y-1 p-2.5 rounded-xl bg-white border border-[#F0DDE4]">
                <span className="text-[10px] uppercase font-bold text-[#77717A] block">
                  Dirección IP Detectada
                </span>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-rose-700 text-sm">
                    {blockedIp || 'IP no detectada'}
                  </span>
                  <span className="px-1.5 py-0.2 text-[9px] font-bold rounded bg-rose-50 text-rose-700 border border-rose-200 uppercase">
                    Denegada
                  </span>
                </div>
              </div>

              <div className="space-y-1 p-2.5 rounded-xl bg-white border border-[#F0DDE4]">
                <span className="text-[10px] uppercase font-bold text-[#77717A] block">
                  Motivo de Restricción
                </span>
                <p className="text-[#302B30] font-medium truncate" title={effectiveReason}>
                  {effectiveReason}
                </p>
              </div>

              {visitorInfo?.model && (
                <div className="space-y-1 p-2.5 rounded-xl bg-white border border-[#F0DDE4]">
                  <span className="text-[10px] uppercase font-bold text-[#77717A] block">
                    Dispositivo Reconocido
                  </span>
                  <div className="flex items-center gap-1.5 text-[#302B30] truncate">
                    <Cpu className="w-3.5 h-3.5 text-[#77717A] shrink-0" />
                    <span className="truncate">{visitorInfo.model}</span>
                  </div>
                </div>
              )}

              <div className="space-y-1 p-2.5 rounded-xl bg-white border border-[#F0DDE4]">
                <span className="text-[10px] uppercase font-bold text-[#77717A] block">
                  Alcance
                </span>
                <p className="text-rose-700 font-bold flex items-center gap-1.5">
                  <WifiOff className="w-3.5 h-3.5 shrink-0" />
                  <span>Acceso Temporalmente Restringido</span>
                </p>
              </div>
            </div>

            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-[11px] text-rose-900 leading-relaxed flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <span>
                Por razones de confidencialidad y resguardo de la pareja, no es posible acceder al contenido privado sin verificación autorizada.
              </span>
            </div>
          </div>

          {/* Discreet Admin Emergency Option */}
          <div className="mt-6 pt-4 border-t border-[#F0DDE4] flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[11px] text-[#77717A] text-center sm:text-left">
              Si consideras que este bloqueo es una equivocación de red, comunícate con la administración.
            </p>

            <button
              onClick={() => {
                setAdminEmailInput('');
                setAdminPasswordInput('');
                setUnlockError('');
                setShowAdminUnlockModal(true);
              }}
              className="text-xs text-[#E83E78] hover:underline font-bold transition-colors inline-flex items-center gap-1.5 py-1.5 px-3 rounded-xl hover:bg-[#FFF4F7] cursor-pointer shrink-0"
              id="btn-admin-emergency-unblock"
            >
              <KeyRound className="w-3.5 h-3.5 text-[#E83E78]" />
              <span>¿Eres el administrador? Desbloquear</span>
            </button>
          </div>
        </motion.div>
      </main>

      {/* Footer Info */}
      <footer className="w-full max-w-2xl text-center z-10 pt-2 pb-2">
        <p className="text-[11px] text-[#77717A] font-mono">
          Firewall Protocol • ID de Incidente: {incidentCode} • Estado: RESTRINGIDO
        </p>
      </footer>

      {/* Modal: Admin Emergency Pin Verification */}
      <AnimatePresence>
        {showAdminUnlockModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/35 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className={`max-w-md w-full bg-white border border-[#F0DDE4] rounded-3xl p-6 shadow-2xl space-y-5 text-[#302B30] ${
                isShaking ? 'animate-shake' : ''
              }`}
            >
              <div className="flex items-center justify-between pb-3 border-b border-[#F0DDE4]">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-[#FFF4F7] text-[#E83E78] border border-[#F0DDE4] flex items-center justify-center">
                    <Lock className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-[#302B30]">Desbloqueo de Administrador</h3>
                    <p className="text-[11px] text-[#77717A]">Verificación con credenciales</p>
                  </div>
                </div>

                <button
                  onClick={() => setShowAdminUnlockModal(false)}
                  className="p-1.5 rounded-lg text-[#77717A] hover:text-[#302B30] hover:bg-[#FFF4F7] transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <p className="text-xs text-[#77717A] leading-relaxed">
                Si eres el administrador y tu IP fue bloqueada por error, introduce tus credenciales para
                remover el bloqueo y restaurar tu acceso de inmediato.
              </p>

              <form onSubmit={handleAdminUnlockSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#302B30] uppercase tracking-wider block">
                    Correo Electrónico
                  </label>
                  <input
                    type="email"
                    required
                    autoFocus
                    placeholder="tu-correo@ejemplo.com"
                    value={adminEmailInput}
                    onChange={(e) => setAdminEmailInput(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white border border-[#F0DDE4] rounded-xl text-sm text-[#302B30] placeholder:text-[#77717A]/50 focus:outline-none focus:border-[#E85D8E] focus:ring-2 focus:ring-[#E85D8E]/15"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#302B30] uppercase tracking-wider block">
                    Contraseña de Administrador
                  </label>
                  <input
                    type="password"
                    required
                    placeholder="••••••••"
                    value={adminPasswordInput}
                    onChange={(e) => setAdminPasswordInput(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white border border-[#F0DDE4] rounded-xl text-sm text-[#302B30] placeholder:text-[#77717A]/50 tracking-wider focus:outline-none focus:border-[#E85D8E] focus:ring-2 focus:ring-[#E85D8E]/15"
                  />
                  {unlockError && (
                    <p className="text-[11px] text-rose-600 font-medium flex items-center gap-1 mt-1">
                      <AlertTriangle className="w-3 h-3 shrink-0" />
                      {unlockError}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAdminUnlockModal(false)}
                    className="px-4 py-2 bg-white hover:bg-[#FFF4F7] border border-[#F0DDE4] text-[#302B30] text-xs font-bold rounded-xl transition-colors cursor-pointer shadow-2xs"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={!adminEmailInput.trim() || !adminPasswordInput.trim() || isUnlocking}
                    className="px-5 py-2 bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-pink-500/25 disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
                  >
                    {isUnlocking ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    )}
                    <span>Validar &amp; Desbloquear</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
